/**
 * Lightweight line-level code coverage for sandbox files.
 *
 * Inserts `__cov$[fileId][lineNo]++` counters at every statement position
 * during transpile via a TypeScript transformer. Counters are TS-line-keyed
 * so reports map directly onto the user's source.
 *
 * ## How it works
 *
 * 1. Before test execution, create a `CoverageTracker`.
 * 2. Pass it to `compileAndLoad()`'s options — the transpiler will call
 *    `tracker.instrument(filePath, tsSource)` instead of the regular
 *    `ts.transpileModule(...)`.
 * 3. After tests finish, call `tracker.collect()` to get per-file,
 *    per-line hit counts and summary statistics.
 *
 * ## Why an AST transformer (not line-by-line text injection)
 *
 * The previous implementation prepended `__cov$[id][N]++;` to every
 * "non-trivial" line of the transpiled JS. That broke any multi-line
 * construct whose continuation lines aren't statements — object literals,
 * class bodies, array literals, switch case labels — yielding invalid JS
 * like:
 *   ```
 *   this.input = {
 *     __cov$["..."][21]++; up: this.keys.has('ArrowUp'),  // <- syntax error
 *   };
 *   ```
 * The TS transformer pass adds counters only at positions where a
 * `Statement` is syntactically valid (Block, SourceFile, CaseClause,
 * DefaultClause), so the resulting JS is always parseable.
 */
import ts from 'typescript';
import { SANDBOX_COMPILER_OPTIONS } from './compilerConfig';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export interface LineCoverage {
  /** 1-based line number in the original TypeScript source */
  line: number;
  /** Number of times this line was executed */
  hits: number;
}

export interface FileCoverage {
  /** Sandbox path, e.g. `/client/systems/InputSystem.ts` */
  path: string;
  /** Per-line hit data (only instrumentable lines included) */
  lines: LineCoverage[];
  /** Total instrumentable lines in the file */
  totalLines: number;
  /** Lines that were hit at least once */
  coveredLines: number;
  /** Coverage percentage (0–100) */
  percentage: number;
}

export interface CoverageReport {
  files: FileCoverage[];
  /** Aggregate across all files */
  totalLines: number;
  coveredLines: number;
  percentage: number;
}

/** Per-file hit counter array. Index = TS line number (1-based, 0 unused). */
type HitCounters = number[];

// ─────────────────────────────────────────────────────────────────────────────
// AST instrumenter — only inserts counters at statement positions
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Build a `__cov$["fileId"][line]++` expression statement.
 * Synthesized nodes are preferred (no source position) so they don't pollute
 * sourcemap-like behavior.
 */
function makeCounterStatement(fileId: string, lineNo: number): ts.Statement {
  return ts.factory.createExpressionStatement(
    ts.factory.createPostfixUnaryExpression(
      ts.factory.createElementAccessExpression(
        ts.factory.createElementAccessExpression(
          ts.factory.createIdentifier('__cov$'),
          ts.factory.createStringLiteral(fileId),
        ),
        ts.factory.createNumericLiteral(lineNo),
      ),
      ts.SyntaxKind.PlusPlusToken,
    ),
  );
}

/**
 * Create a TS transformer that prepends a counter increment before every
 * statement in every Block, CaseClause, DefaultClause, and the SourceFile.
 *
 * `onInstrumented` is called with the TS line number (1-based) for each
 * statement that gets a counter, so the tracker can record which lines
 * are eligible for the coverage report.
 */
function createInstrumentTransformer(
  fileId: string,
  onInstrumented: (tsLineNo: number) => void,
): ts.TransformerFactory<ts.SourceFile> {
  return (context) => (sourceFile) => {
    function instrumentStatements(stmts: readonly ts.Statement[]): ts.Statement[] {
      const out: ts.Statement[] = [];
      for (const stmt of stmts) {
        // Capture line BEFORE recursive visit; nested visits may swap the
        // node and synthesized replacements have no source position.
        let tsLine: number | null = null;
        if (stmt.pos >= 0) {
          try {
            const start = stmt.getStart(sourceFile);
            tsLine = sourceFile.getLineAndCharacterOfPosition(start).line + 1;
          } catch {
            tsLine = null;
          }
        }
        if (tsLine !== null) {
          onInstrumented(tsLine);
          out.push(makeCounterStatement(fileId, tsLine));
        }
        out.push(ts.visitNode(stmt, visitor) as ts.Statement);
      }
      return out;
    }

    const visitor: ts.Visitor = (node) => {
      // Containers whose `statements` array can hold our counter statements.
      if (ts.isBlock(node)) {
        return ts.factory.updateBlock(node, instrumentStatements(node.statements));
      }
      if (ts.isCaseClause(node)) {
        return ts.factory.updateCaseClause(
          node,
          node.expression,
          instrumentStatements(node.statements),
        );
      }
      if (ts.isDefaultClause(node)) {
        return ts.factory.updateDefaultClause(node, instrumentStatements(node.statements));
      }
      if (ts.isSourceFile(node)) {
        return ts.factory.updateSourceFile(node, instrumentStatements(node.statements));
      }
      // Recurse — but DON'T instrument inside class bodies or object literals;
      // ClassBody is visited via visitEachChild but its members are not
      // `Statement` nodes, so they pass through unchanged.
      return ts.visitEachChild(node, visitor, context);
    };

    return ts.visitNode(sourceFile, visitor) as ts.SourceFile;
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// CoverageTracker
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Tracks line-level code coverage across sandbox file executions.
 *
 * Usage:
 * ```ts
 * const tracker = new CoverageTracker();
 * compileAndLoad(entry, files, cache, externals, console, { coverageTracker: tracker });
 * const report = tracker.collect(originalFiles);
 * ```
 */
export class CoverageTracker {
  /** Per-file hit counter arrays, keyed by sandbox path. */
  private readonly counters = new Map<string, HitCounters>();
  /** Set of TS line numbers that were instrumented per file. */
  private readonly instrumentedLines = new Map<string, Set<number>>();

  /** The global object injected into sandboxed code as `__cov$`. */
  readonly global: Record<string, HitCounters> = {};

  /**
   * Transpile and instrument a TS source file. Returns the resulting JS.
   *
   * @param filePath  Sandbox path (e.g. `/client/systems/InputSystem.ts`)
   * @param tsSource  Original TypeScript source
   */
  instrument(filePath: string, tsSource: string): string {
    const fileId = this.sanitizeId(filePath);
    const tsLineCount = tsSource.split('\n').length;

    // (Re)initialize counter array for this file. +1 for 1-based indexing.
    const hitArray: HitCounters = new Array(tsLineCount + 1).fill(0);
    this.counters.set(filePath, hitArray);
    this.global[fileId] = hitArray;

    const instrumented = new Set<number>();
    this.instrumentedLines.set(filePath, instrumented);

    const transformer = createInstrumentTransformer(fileId, (tsLineNo) => {
      // Bounds-check defensively; if the transformer ever sees a line beyond
      // the file's length (synthesized constructs etc.), grow the array.
      if (tsLineNo >= hitArray.length) {
        for (let i = hitArray.length; i <= tsLineNo; i++) hitArray.push(0);
      }
      instrumented.add(tsLineNo);
    });

    const result = ts.transpileModule(tsSource, {
      compilerOptions: { ...SANDBOX_COMPILER_OPTIONS },
      fileName: filePath,
      transformers: { before: [transformer] },
    });

    return result.outputText;
  }

  /**
   * Collect coverage data and produce a report.
   *
   * @param originalFiles  Map of sandbox paths → original TS source code.
   *                       (Kept for API compatibility; not strictly required
   *                       now that line numbers are tracked in TS-source space.)
   */
  collect(originalFiles: Map<string, string>): CoverageReport {
    const files: FileCoverage[] = [];
    let totalLines = 0;
    let coveredLines = 0;

    for (const [filePath, hitArray] of this.counters) {
      const instrumented = this.instrumentedLines.get(filePath);
      if (!instrumented) continue;

      // Skip files we don't have source for (caller may filter out test files).
      if (!originalFiles.has(filePath)) continue;

      const lines: LineCoverage[] = [];
      for (const tsLineNo of instrumented) {
        const hits = hitArray[tsLineNo] ?? 0;
        lines.push({ line: tsLineNo, hits });
      }
      lines.sort((a, b) => a.line - b.line);

      const fileTotalLines = lines.length;
      const fileCoveredLines = lines.filter((l) => l.hits > 0).length;
      const percentage =
        fileTotalLines > 0
          ? Math.round((fileCoveredLines / fileTotalLines) * 10000) / 100
          : 100;

      files.push({
        path: filePath,
        lines,
        totalLines: fileTotalLines,
        coveredLines: fileCoveredLines,
        percentage,
      });

      totalLines += fileTotalLines;
      coveredLines += fileCoveredLines;
    }

    files.sort((a, b) => a.path.localeCompare(b.path));

    const overallPercentage =
      totalLines > 0 ? Math.round((coveredLines / totalLines) * 10000) / 100 : 100;

    return { files, totalLines, coveredLines, percentage: overallPercentage };
  }

  /** Reset all counters (for running a fresh set of tests). */
  reset(): void {
    this.counters.clear();
    this.instrumentedLines.clear();
    for (const key of Object.keys(this.global)) {
      delete this.global[key];
    }
  }

  /** Format a coverage report as a human-readable summary string. */
  static formatReport(report: CoverageReport): string {
    const lines: string[] = [];
    lines.push(
      `Coverage: ${report.coveredLines}/${report.totalLines} lines (${report.percentage}%)`,
    );
    lines.push('');

    for (const file of report.files) {
      const bar = file.percentage >= 80 ? '✓' : file.percentage >= 50 ? '△' : '✗';
      lines.push(
        `${bar} ${file.path}: ${file.coveredLines}/${file.totalLines} (${file.percentage}%)`,
      );

      if (file.percentage < 100) {
        const uncovered = file.lines.filter((l) => l.hits === 0).map((l) => l.line);
        if (uncovered.length > 0) {
          const ranges = compactRanges(uncovered);
          lines.push(`  uncovered: ${ranges}`);
        }
      }
    }

    return lines.join('\n');
  }

  /** Convert file path to a safe JS identifier for use as object key. */
  private sanitizeId(filePath: string): string {
    return filePath.replace(/[^a-zA-Z0-9]/g, '_');
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

/** Compact [1,2,3,5,7,8,9] → "1-3, 5, 7-9" */
function compactRanges(sorted: number[]): string {
  if (sorted.length === 0) return '';
  const ranges: string[] = [];
  let start = sorted[0];
  let end = start;

  for (let i = 1; i < sorted.length; i++) {
    if (sorted[i] === end + 1) {
      end = sorted[i];
    } else {
      ranges.push(start === end ? `${start}` : `${start}-${end}`);
      start = sorted[i];
      end = start;
    }
  }
  ranges.push(start === end ? `${start}` : `${start}-${end}`);
  return ranges.join(', ');
}
